import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  AlertTypes,
  baseCssConfigStyle,
  CardPaymentStatus,
  CardPaymentType,
  CardTrxStatusFilterOptions,
  CardTrxTypeFilterOptions,
  CurrencyExchangeTransactionType,
  DatePickerTypes,
  TransactionFilterOptions,
  TransactionStatus,
  TransactionType,
  TrxStatusIncomingFilterOptions,
  TrxStatusOutgoingFilterOptions,
  TrxTypeFilterOptions,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Subject, takeUntil } from 'rxjs';
import { formatDateFromDateTime, isFutureDate } from '../../../../lib/utils/date-handling.utils';

export type FilterChangeEvent = {
  filterType: string;
  value: any;
  isActive: boolean;
};

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-filter-container',
  templateUrl: './filter-container.component.html',
  styleUrls: ['./filter-container.component.scss'],
})
export class FilterContainerComponent implements OnInit, OnChanges {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  @Input() currentTransactionType!: TransactionType | CurrencyExchangeTransactionType;
  @Input() isCardTransactions!: boolean;
  @Input() filterOptions: { name: string; value: string; isActive: boolean }[] = [];
  @Input() filtersEnabled = false;
  @Input() filterMarginStyle: baseCssConfigStyle = {};
  @Input() attri: UiZoneWidgetAttributeConfig;
  @Input() dateFrom: string | null = null;
  @Input() dateTo: string | null = null;

  // Emit filter changes to parent
  @Output() filterChange = new EventEmitter<FilterChangeEvent>();
  @Output() dateRangeChange = new EventEmitter<{ dateFrom: string; dateTo: string }>();
  @Output() statusSelectionChange = new EventEmitter<CardPaymentStatus[] | TransactionStatus[]>();
  @Output() typeSelectionChange = new EventEmitter<CardPaymentType[] | TransactionType>();

  public readonly TransactionFilterOptions = TransactionFilterOptions;
  public readonly datePickerTypes = DatePickerTypes;
  public fromDateControl = new FormControl();
  public toDateControl = new FormControl();
  private selectDateFrom: string | null = null;
  private selectDateTo: string | null = null;
  public statusOptions: any[] = [];
  public typeOptions: any[] = [];

  constructor(private alertService: AlertHandlerService) {}

  ngOnInit(): void {
    if (this.isCardTransactions) {
      this.typeOptions = CardTrxTypeFilterOptions;
      this.statusOptions = CardTrxStatusFilterOptions;
    } else {
      this.typeOptions = TrxTypeFilterOptions;
      this.updateStatusOptionsForTransactionType();
    }

    this.fromDateControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value && isFutureDate(value)) {
        this.alertService.showAlertFn(AlertTypes.WARNING, 'Start date cannot be in the future');

        return this.fromDateControl.setValue(null, { emitEvent: false });
      }
      this.selectDateFrom = value ? formatDateFromDateTime(value) : null;
      this.checkAndApplyDateRange();
    });
    this.toDateControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value && isFutureDate(value)) {
        this.alertService.showAlertFn(AlertTypes.WARNING, 'End date cannot be in the future');

        return this.toDateControl.setValue(null, { emitEvent: false });
      }
      this.selectDateTo = value ? formatDateFromDateTime(value) : null;
      this.checkAndApplyDateRange();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // React to transaction type changes
    if (changes['currentTransactionType'] && !changes['currentTransactionType'].isFirstChange()) {
      this.updateStatusOptionsForTransactionType();
    }
  }

  private updateStatusOptionsForTransactionType() {
    switch (this.currentTransactionType) {
      case TransactionType.OUTGOING:
        this.statusOptions = [...TrxStatusOutgoingFilterOptions];
        break;
      case TransactionType.INCOMING:
        this.statusOptions = [...TrxStatusIncomingFilterOptions];
        break;
      default:
        // For currency exchange and generic (all) transactions, no status options not filtering possible
        this.statusOptions = [];
        break;
    }
  }

  public onFilterPillChange(filter: { name: string; value: string; isActive: boolean }) {
    this.filterOptions = this.filterOptions?.map((f) => {
      return f.value === filter.value ? { ...f, isActive: filter.isActive } : f;
    });

    if (!filter.isActive) {
      this.clearFilterSelections(filter.value);
    }
    this.filterChange.emit({
      filterType: filter.value,
      value: filter.isActive,
      isActive: filter.isActive,
    });
  }

  public onSubFilterPillChange(
    filter: { name: string; value: string; isActive: boolean },
    filterType: string,
  ) {
    switch (filterType) {
      case TransactionFilterOptions.STATUS:
        this.statusOptions = this.statusOptions.map((option) =>
          option.value === filter.value ? { ...option, isActive: filter.isActive } : option,
        );
        const selectedStatuses = this.statusOptions
          .filter((option) => option.isActive)
          .map((option) => option.value);
        this.statusSelectionChange.emit(selectedStatuses);
        break;

      case TransactionFilterOptions.TYPE:
        this.typeOptions = this.typeOptions.map((option) =>
          option.value === filter.value ? { ...option, isActive: filter.isActive } : option,
        );
        const selectedTypes = this.typeOptions
          .filter((option) => option.isActive)
          .map((option) => option.value);
        this.typeSelectionChange.emit(selectedTypes);
        break;
    }
  }

  public onDateRangeChange(dateRange: { dateFrom: string; dateTo: string }) {
    this.dateFrom = dateRange.dateFrom;
    this.dateTo = dateRange.dateTo;
    this.dateRangeChange.emit(dateRange);

    this.filterChange.emit({
      filterType: TransactionFilterOptions.DATE,
      value: dateRange,
      isActive: true,
    });
  }

  public isFilterActive(filterValue: string): boolean {
    return this.filterOptions?.some((f) => f.value === filterValue && f.isActive);
  }

  public hasActiveFilters(): boolean {
    return this.filterOptions?.some((f) => f.isActive);
  }

  private checkAndApplyDateRange() {
    // Auto-apply when both dates are selected
    if (this.selectDateFrom && this.selectDateTo) {
      this.onDateRangeChange({
        dateFrom: this.selectDateFrom,
        dateTo: this.selectDateTo,
      });
    }
  }

  private clearFilterSelections(filterValue: string) {
    switch (filterValue) {
      case TransactionFilterOptions.DATE:
        this.fromDateControl.setValue(null);
        this.toDateControl.setValue(null);
        this.selectDateFrom = null;
        this.selectDateTo = null;
        this.dateFrom = null;
        this.dateTo = null;
        break;
      case TransactionFilterOptions.STATUS:
        this.statusOptions = this.statusOptions.map((option) => ({ ...option, isActive: false }));
        this.statusSelectionChange.emit([]);
        break;
      case TransactionFilterOptions.TYPE:
        this.typeOptions = this.typeOptions.map((option) => ({ ...option, isActive: false }));
        this.typeSelectionChange.emit([]);
        break;
      default:
        console.warn('Unhandled filter type:', filterValue);
    }
  }
}
